import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from './ToastContext'
import { BottomSheet, Field } from './ui'
import type { Build, Product } from '../lib/database.types'

export interface MarkSoldSheetProps {
  open: boolean
  onClose: () => void
  build: Build
  product: Product | null
  // For displaying / cost-basis context — printer name etc.
  costBasis?: number
}

export function MarkSoldSheet({ open, onClose, build, product, costBasis }: MarkSoldSheetProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [price, setPrice] = useState(
    product?.default_unit_price != null ? String(product.default_unit_price) : '',
  )
  const today = new Date().toISOString().slice(0, 10)
  const [saleDate, setSaleDate] = useState(today)
  const [platform, setPlatform] = useState('local-cash')
  const [customerName, setCustomerName] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  if (!open) return null

  const handleSave = async () => {
    setSaving(true)
    setErr('')
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const amount = Number(price)
      if (isNaN(amount) || amount <= 0) {
        setErr('Enter a valid price.')
        setSaving(false)
        return
      }
      // 1) Create the sale row. Explicitly set every field the canonical
      // schema knows about so this code path matches useCreateSale —
      // platform_fees / shipping / purpose / is_refunded need values, not
      // implicit defaults, so they round-trip into refund + profit math
      // the same way as sales created from the full form.
      const { data: sale, error: saleErr } = await supabase
        .from('sales')
        .insert({
          user_id: user.id,
          sale_date: saleDate,
          gross_amount: amount,
          platform_fees: 0,
          shipping_charged: 0,
          shipping_cost: 0,
          platform,
          customer_name: customerName.trim() || null,
          notes: null,
          purpose: 'normal',
          is_refunded: false,
        })
        .select('*')
        .single()
      if (saleErr) throw saleErr
      // 2) Line item linking the sale to the product + build.
      const { error: liErr } = await supabase.from('sale_line_items').insert({
        user_id: user.id,
        sale_id: sale.id,
        product_id: product?.id ?? null,
        description: product?.name ?? build.name,
        quantity: build.units,
        unit_price: amount / Math.max(1, build.units),
      })
      if (liErr) throw liErr
      // Invalidate caches so inventory + recoup refresh.
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.invalidateQueries({ queryKey: ['sale_line_items'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['builds'] })
      toast({ tone: 'success', text: `Sold ${product?.name ?? build.name}` })
      onClose()
      navigate('/money')
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const profitHint =
    price && costBasis != null && Number(price) > 0
      ? `Margin: $${(Number(price) - costBasis).toFixed(2)} (cost basis $${costBasis.toFixed(2)})`
      : ''

  return (
    <BottomSheet open={open} onClose={onClose} title={`Mark sold — ${product?.name ?? build.name}`}>
      <Field label="Sale price ($)" value={price} onChange={setPrice} type="decimal" required />
      {profitHint && (
        <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-xs)', marginTop: -8, marginBottom: 12 }}>
          {profitHint}
        </div>
      )}
      <Field label="Sale date" value={saleDate} onChange={setSaleDate} type="date" />
      <Field
        label="Platform"
        value={platform}
        onChange={setPlatform}
        select={['local-cash', 'etsy', 'shopify', 'shopify-pos', 'farmers-market', 'other']}
      />
      <Field label="Customer (optional)" value={customerName} onChange={setCustomerName} />
      {err && <div style={{ color: 'var(--color-danger-text)', fontSize: 'var(--font-size-sm)', marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            flex: 1,
            padding: 'var(--space-3)',
            background: 'transparent',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 2,
            padding: 'var(--space-3)',
            background: saving ? 'var(--color-text-faint)' : 'var(--color-success)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: 'white',
            fontWeight: 700,
            fontSize: 'var(--font-size-sm)',
            cursor: saving ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {saving ? 'Saving…' : '💰 Record sale'}
        </button>
      </div>
    </BottomSheet>
  )
}
